import React, { useRef } from 'react';
import {
  BarcodeOutlined,
  FileTextOutlined,
  GiftOutlined,
  InsertRowBelowOutlined,
  TagsOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { usePrintQueue } from '../../context/PrintQueueContext';
import {
  getPrintBundlePaperAvailability,
  PRINT_QUEUE_ITEM_STATES,
} from '../../services/printQueueModel';
import './PrintQueuePanel.css';

const paperTypes = [
  { key: 'pickList', label: 'Wave pick list', Icon: UnorderedListOutlined },
  { key: 'packingSlip', label: 'Packing slip', Icon: FileTextOutlined },
  { key: 'internalLabel', label: 'Internal sticky label', Icon: TagsOutlined },
  { key: 'inscription', label: 'A6 inscription', Icon: GiftOutlined },
  { key: 'carrierLabel', label: 'Carrier shipping label', Icon: BarcodeOutlined },
  { key: 'separator', label: 'Separator sheet', Icon: InsertRowBelowOutlined },
];

function BundlePaperAvailability({ bundle }) {
  const availability = getPrintBundlePaperAvailability(bundle);
  return (
    <div className="print-queue-papers" aria-label="Available paper items">
      <span className="print-queue-papers__label">Papers</span>
      {paperTypes.map(({ key, label, Icon }) => {
        const available = availability[key];
        return (
          <span
            key={key}
            className={`print-queue-paper${available ? ' is-available' : ' is-unavailable'}`}
            title={`${label}: ${available ? 'available in this bundle' : 'not added'}`}
            aria-label={`${label}: ${available ? 'available' : 'not added'}`}
          >
            <Icon />
            <span>{label}</span>
            <i aria-hidden="true" />
          </span>
        );
      })}
    </div>
  );
}

function QueueItem({
  item,
  bundleId,
  index,
  moveItem,
  acknowledgeItemWarning,
  itemDragRef,
}) {
  const movable = [PRINT_QUEUE_ITEM_STATES.STAGED, PRINT_QUEUE_ITEM_STATES.PAUSED].includes(
    item.status,
  );
  return (
    <li
      draggable={movable}
      onDragStart={() => {
        itemDragRef.current = { bundleId, index };
      }}
      onDragOver={(event) => {
        if (itemDragRef.current?.bundleId === bundleId) event.preventDefault();
      }}
      onDrop={() => {
        const dragged = itemDragRef.current;
        if (!dragged || dragged.bundleId !== bundleId || dragged.index === index) return;
        moveItem(bundleId, dragged.index, index);
        itemDragRef.current = null;
      }}
      className={`print-queue-item is-${item.status}`}
    >
      <span className="print-queue-item__handle" aria-hidden="true">⋮⋮</span>
      <span>
        <b>{item.label}</b>
        <small>
          {item.routing?.printer || 'No printer'} ·{' '}
          {item.routing?.printerTray || 'driver default'} · {item.profileKey}
        </small>
      </span>
      <span className="print-queue-item__status">{item.status}</span>
      {item.error && <p className="print-queue-item__error">{item.error}</p>}
      {item.requiresAcknowledgement && (
        <button
          type="button"
          className="toolkit-btn toolkit-btn--secondary"
          onClick={() => acknowledgeItemWarning(item.id)}
        >
          Review warning and continue
        </button>
      )}
      {item.status === PRINT_QUEUE_ITEM_STATES.AWAITING_CONFIRMATION && (
        <small className="print-queue-item__auto-clear">
          Clears automatically in about 30 seconds
        </small>
      )}
    </li>
  );
}

function QueueBundle({
  bundle,
  index,
  moveBundle,
  moveItem,
  removeBundle,
  acknowledgeItemWarning,
  bundleDragRef,
  itemDragRef,
}) {
  const movable = bundle.items.every((item) =>
    [PRINT_QUEUE_ITEM_STATES.STAGED, PRINT_QUEUE_ITEM_STATES.PAUSED].includes(item.status),
  );
  return (
    <article
      draggable={movable}
      onDragStart={(event) => {
        if (event.target.closest?.('.print-queue-item')) {
          event.stopPropagation();
          return;
        }
        bundleDragRef.current = index;
      }}
      onDragOver={(event) => {
        if (bundleDragRef.current != null) event.preventDefault();
      }}
      onDrop={() => {
        if (bundleDragRef.current == null || bundleDragRef.current === index) return;
        moveBundle(bundleDragRef.current, index);
        bundleDragRef.current = null;
      }}
      className="print-queue-bundle"
    >
      <header>
        <span className="print-queue-bundle__handle" aria-hidden="true">⋮⋮</span>
        <div>
          <h4>{index + 1}. {bundle.label}</h4>
          <p>{bundle.items.length} required part{bundle.items.length === 1 ? '' : 's'}</p>
        </div>
        {movable ? (
          <button type="button" className="toolkit-btn toolkit-btn--ghost" onClick={() => removeBundle(bundle.id)}>
            Remove
          </button>
        ) : null}
      </header>
      {bundle.auditError && (
        <p className="print-queue-panel__warning">{bundle.auditError}</p>
      )}
      <BundlePaperAvailability bundle={bundle} />
      <ol>
        {bundle.items.map((item, itemIndex) => (
          <QueueItem
            key={item.id}
            item={item}
            bundleId={bundle.id}
            index={itemIndex}
            moveItem={moveItem}
            acknowledgeItemWarning={acknowledgeItemWarning}
            itemDragRef={itemDragRef}
          />
        ))}
      </ol>
    </article>
  );
}

export default function PrintQueuePanel() {
  const queue = usePrintQueue();
  const bundleDragRef = useRef(null);
  const itemDragRef = useRef(null);
  const { state } = queue;
  const itemCount = state.bundles.reduce((sum, bundle) => sum + bundle.items.length, 0);
  const runnableCount = state.bundles
    .flatMap((bundle) => bundle.items)
    .filter((item) =>
      [PRINT_QUEUE_ITEM_STATES.STAGED, PRINT_QUEUE_ITEM_STATES.PAUSED].includes(
        item.status,
      ),
    ).length;
  const awaitingClearCount = state.bundles
    .flatMap((bundle) => bundle.items)
    .filter(
      (item) => item.status === PRINT_QUEUE_ITEM_STATES.AWAITING_CONFIRMATION,
    ).length;
  const uncertain = state.bundles
    .flatMap((bundle) => bundle.items)
    .find((item) => item.id === state.blockedItemId);
  const lanes = state.bundles
    .flatMap((bundle) =>
      bundle.items.map((item) => ({
        printer: item.routing?.printer || 'Unassigned printer',
        label: `${bundle.label}: ${item.label}`,
      })),
    )
    .reduce((map, item) => {
      if (!map[item.printer]) map[item.printer] = [];
      map[item.printer].push(item.label);
      return map;
    }, {});

  const handleClearQueue = async () => {
    if (!state.bundles.length || state.running) return;
    const confirmed = window.confirm(
      'Clear the entire print queue?\n\nThis removes all order bundles and stored PDFs from this workstation. It does not stop anything already sent to the printer.',
    );
    if (!confirmed) return;
    try {
      await queue.clearQueue();
    } catch (error) {
      window.alert(error?.message || 'The print queue could not be cleared.');
    }
  };

  return (
      <section className="print-queue-panel">
        <header className="print-queue-panel__header">
          <div>
            <p className="print-queue-panel__eyebrow">London Smoke &amp; Cure</p>
            <h3>Mixed order print queue</h3>
            <p>{state.bundles.length} order bundle{state.bundles.length === 1 ? '' : 's'} · {itemCount} spool{itemCount === 1 ? '' : 's'}</p>
            <p className="print-queue-panel__stack-hint">Physical stack: pick list (if queued) first, then slips / Cheetah, inscriptions, carrier labels; Start-of segment sheets print after each shipping group and land on top of that set.</p>
          </div>
          <div className="print-queue-panel__actions">
            {!state.running ? (
              <button type="button" className="toolkit-btn toolkit-btn--primary" disabled={!runnableCount || state.blockedItemId} onClick={state.pauseRequested ? queue.resumeQueue : queue.startQueue}>
                {state.pauseRequested ? 'Resume' : 'Start'}
              </button>
            ) : (
              <button type="button" className="toolkit-btn toolkit-btn--ghost" onClick={queue.pauseQueue}>
                Pause after current
              </button>
            )}
            <button type="button" className="toolkit-btn toolkit-btn--ghost" onClick={queue.cancelPending}>
              Cancel pending
            </button>
            <button
              type="button"
              className="toolkit-btn toolkit-btn--ghost print-queue-panel__clear"
              onClick={handleClearQueue}
              disabled={!state.bundles.length || state.running}
              title={
                state.running
                  ? 'Pause or wait for the current print to finish before clearing'
                  : 'Remove all order bundles and stored PDFs'
              }
            >
              Clear queue
            </button>
          </div>
        </header>

        {queue.persistenceWarning && <p className="print-queue-panel__warning">{queue.persistenceWarning}</p>}
        {queue.storageEstimate.ratio > 0.7 && (
          <p className="print-queue-panel__warning">
            Browser storage is over 70% full. Remove completed or unused PDF jobs.
          </p>
        )}
        <details className="print-queue-panel__setup">
          <summary>Setup</summary>
          <div className="print-queue-panel__settings">
            <label>
              Settle delay after QZ acceptance
              <input type="number" min="0" max="10000" step="250" value={state.settleDelayMs} onChange={(event) => queue.setSettleDelay(event.target.value)} />
              <small>milliseconds · then the next spool starts; finished bundles auto-clear after 30 seconds</small>
            </label>
            <p>
              IndexedDB usage:{' '}
              <b>{(queue.storageEstimate.usage / 1024 / 1024).toFixed(1)} MB</b>
              {queue.storageEstimate.quota > 0
                ? ` of ${(queue.storageEstimate.quota / 1024 / 1024).toFixed(0)} MB`
                : ''}
            </p>
          </div>
        </details>

        {uncertain && (
          <div className="print-queue-panel__blocked" role="alert">
            <b>Queue blocked: {uncertain.label}</b>
            <p>{uncertain.error}</p>
            <div>
              <button type="button" onClick={() => queue.resolveUncertain(uncertain.id, 'produced')}>It printed</button>
              <button type="button" onClick={() => queue.resolveUncertain(uncertain.id, 'retry')}>It did not print, retry</button>
              <button type="button" onClick={() => queue.resolveUncertain(uncertain.id, 'skip')}>Skip it</button>
              <button type="button" onClick={() => queue.resolveUncertain(uncertain.id, 'abort')}>Abort remaining</button>
            </div>
          </div>
        )}

        {awaitingClearCount > 0 && !state.running && !uncertain && (
          <p className="print-queue-panel__auto-clear" role="status">
            {awaitingClearCount} finished spool{awaitingClearCount === 1 ? '' : 's'} will leave the queue automatically after about 30 seconds.
          </p>
        )}

        {!state.bundles.length ? (
          <div className="print-queue-panel__empty">
            Configure packing slips, inscriptions, or carrier labels, then choose Add to print queue.
            Print now / direct QZ jobs skip this queue.
          </div>
        ) : (
          <div className="print-queue-panel__pile">
            {state.bundles.map((bundle, index) => (
              <QueueBundle
                key={bundle.id}
                bundle={bundle}
                index={index}
                moveBundle={queue.moveBundle}
                moveItem={queue.moveItem}
                removeBundle={queue.removeBundle}
                acknowledgeItemWarning={queue.acknowledgeItemWarning}
                bundleDragRef={bundleDragRef}
                itemDragRef={itemDragRef}
              />
            ))}
          </div>
        )}

        {Object.keys(lanes).length > 1 && (
          <section className="print-queue-pickup">
            <h4>Collection order by printer</h4>
            <p>Submission order is guaranteed. Independent printers may physically finish at different times.</p>
            <div>
              {Object.entries(lanes).map(([printer, labels]) => (
                <article key={printer}>
                  <b>{printer}</b>
                  <ol>{labels.map((label, index) => <li key={`${label}-${index}`}>{label}</li>)}</ol>
                </article>
              ))}
            </div>
          </section>
        )}
      </section>
  );
}
